import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import BodyText from 'LuxKit/Typography/BodyText'
import Table from 'LuxKit/Tables/Table'
import TableHeader from 'LuxKit/Tables/TableHeader'
import TableHeaderRow from 'LuxKit/Tables/TableHeaderRow'
import TableHeaderCell from 'LuxKit/Tables/TableHeaderCell'
import TableBody from 'LuxKit/Tables/TableBody'
import TableBodyCell from 'LuxKit/Tables/TableBodyCell'
import TableBodyRow from 'LuxKit/Tables/TableBodyRow'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import FormatDecimal from 'components/utils/Formatters/FormatDecimal'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import Colour from 'components/utils/Colour'
import LineShoppingBagIcon from 'LuxKit/Icons/line/LineShoppingBagIcon'

interface Props {
  offerName: string
  quantity: number
  pointsCalculation?: Omit<App.LoyaltyPointsEarnCalculation, 'request'>
}

function MerchandisePointsPayCalculatorInfoModal({
  offerName,
  quantity,
  pointsCalculation,
}: Props) {
  const intl = useIntl()
  const programName = getLoyaltyProgramDisplayName()

  const earnableBasePoints = pointsCalculation?.basePoints ?? 0
  const earnableBonusPoints = pointsCalculation?.bonusPoints ?? 0
  const earnableStatusCredits = pointsCalculation?.statusCredits ?? 0

  const hasAnyBonusPoints = earnableBonusPoints > 0

  return <ModalBase dismissible size="S">
    <ModalHeader title={intl.formatMessage({
      id: 'qApT400r4b',
      defaultMessage: 'Earning points',
      description: 'Modal title for points earning breakdown',
    })}
    />
    <ModalBody>
      <ModalContent>
        <VerticalSpacer gap={16}>
          <BodyText variant="medium">
            {intl.formatMessage({
              id: '9AH+ydT4gw',
              defaultMessage: 'Below is a breakdown of the points earned for this product.',
              description: 'Description text for points earning breakdown modal',
            })}
          </BodyText>

          <Table>
            <TableHeader>
              <TableHeaderRow>
                <TableHeaderCell width={288}>
                  {intl.formatMessage({
                    id: '/lge8vj0CL',
                    defaultMessage: 'Product',
                    description: 'Table column header for product name',
                  })}
                </TableHeaderCell>
                <TableHeaderCell>
                  {intl.formatMessage({
                    id: 'ObaeltNTZb',
                    defaultMessage: 'Points',
                    description: 'Table column header for base points',
                  })}
                </TableHeaderCell>
                {hasAnyBonusPoints && <TableHeaderCell minWidth={92}>
                  <Colour value="highlight-secondary">
                    {intl.formatMessage({
                      id: 'JKPfGJZtru',
                      defaultMessage: 'Bonus Points',
                      description: 'Table column header for bonus points',
                    })}
                  </Colour>
                </TableHeaderCell>}
                <TableHeaderCell minWidth={106}>
                  {intl.formatMessage({
                    id: 'b8Y2vT7ol3',
                    defaultMessage: 'Status Credits',
                    description: 'Table column header for status credits',
                  })}
                </TableHeaderCell>
              </TableHeaderRow>
            </TableHeader>
            <TableBody>
              <TableBodyRow>
                <TableBodyCell>
                  <BodyTextBlock
                    variant="medium"
                    colour="neutral-two"
                    startIcon={<LineShoppingBagIcon />}
                    format="propercase"
                  >
                    {offerName}
                    {quantity > 1 && ` (x${quantity})`}
                  </BodyTextBlock>
                </TableBodyCell>
                <TableBodyCell>
                  <BodyText variant="medium" colour="neutral-two">
                    {earnableBasePoints ? <FormatDecimal value={earnableBasePoints} /> : <>--</>}
                  </BodyText>
                </TableBodyCell>
                {hasAnyBonusPoints && <TableBodyCell>
                  <BodyText variant="medium">
                    <Colour value="highlight-secondary">
                      <FormatDecimal value={earnableBonusPoints} />
                    </Colour>
                  </BodyText>
                </TableBodyCell>}
                <TableBodyCell>
                  <BodyText variant="medium" colour="neutral-two">
                    {earnableStatusCredits ? <FormatDecimal value={earnableStatusCredits} /> : <>--</>}
                  </BodyText>
                </TableBodyCell>
              </TableBodyRow>
            </TableBody>
          </Table>

          <BodyText variant="small" colour="neutral-two">
            {intl.formatMessage({
              id: '3bjRPENLxD',
              defaultMessage: 'Please note that the final points balance will be credited to your account within 30-40 days after your Shop order is confirmed, and may be different from what you see here.',
              description: 'Disclaimer about points credit timing',
            })}
            <br /><br />
            <FormattedMessage
              id="d7fAh40sYL"
              defaultMessage="You will not earn points on any portion or whole item that is paid for with points. Points can only be earned on the remaining dollar value. Some payments types are not eligible for earning points such as promotional codes, credits received before {programName} launched, credits received due to cancellation of a booking before {programName} launched."
              description="Disclaimer about points earning restrictions"
              values={{ programName }}
            />
          </BodyText>
        </VerticalSpacer>
      </ModalContent>
    </ModalBody>
  </ModalBase>
}

export default MerchandisePointsPayCalculatorInfoModal
